import { Spinner } from "@cynthai/ui";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { setAuthMethod, setTokenExpiry } from "@/core/lib/auth-session";
import { workosCallback } from "@/openapi";
import { getCurrentUserQueryKey } from "@/openapi/@tanstack/react-query.gen";

const searchSchema = z.object({
  code: z.string(),
  state: z.string().optional(),
});

function isSafeRedirect(value: string | undefined): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.startsWith("/auth/")
  );
}

function WorkosCallback() {
  return (
    <div className="flex items-center justify-center">
      <Spinner size="md" />
    </div>
  );
}

export const Route = createFileRoute("/auth/workos/callback")({
  component: () => <WorkosCallback />,
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    if (context.user) {
      throw redirect({ to: "/" });
    }

    const { data } = await workosCallback({
      body: { code: search.code },
      throwOnError: true,
    });

    setAuthMethod("workos");
    setTokenExpiry(data.expires_in);

    // /me is persisted and the protected loader serves it for 30s, so a record
    // left by whoever was signed in before this exchange would answer the gate
    // checks for the identity that just replaced them
    context.queryClient.removeQueries({ queryKey: getCurrentUserQueryKey() });

    // terms acceptance is not routed from here: the protected loader gates on
    // /me, which rejects a missing 2fa setup before it rejects missing terms,
    // so sending sso users straight to /auth/terms would strand them on a page
    // whose accept call they cannot pass
    throw redirect({
      to: isSafeRedirect(search.state) ? search.state : "/",
      replace: true,
    });
  },
});
